import type { TFunction } from 'i18next'

import { ApiError } from '@/lib/api'
import { describeProblem } from '@/lib/problems'

/* The case screen's own refusal wording, next to the case screen rather than in
 * `lib/refusals.ts`. That module is the DOCUMENT pipeline's sentence — the FR-64
 * branding refusal three features share — and this one is the case's two named
 * 409s. Neither is a superset of the other, so they stay two functions; this one
 * lives here because the sections that need it are no longer one file. */

/** A server refusal as one line, in the reader's language — except when the
 *  server's own words are the answer.
 *
 *  Ordinarily this is `describeProblem`'s translated key and never the problem's
 *  `detail`, which is written server-side in English for a log (AD-22, AD-27).
 *  A 409 on this page is the documented exception, and there are two of them:
 *  deleting a leg that carries costs (FR-67) and closing a case with unsettled
 *  balances (FR-25). Both refusals exist precisely to NAME what is blocking —
 *  "it carries Border truck Khorgos → Sarahs: 1 800 USD" — and those names are
 *  finance's own verbatim phrasing, which `casefile/errors.go` insists must not
 *  be reworded because the user checks them against the money screen. A
 *  translated "there is a conflict" would throw away the whole content of the
 *  control.
 *
 *  The right long-term shape is the names as structured data with the frame
 *  translated around them, which needs `httpx.Conflict` to carry a list — a
 *  change in files this section does not own. Until then the server's sentence
 *  is strictly better than discarding it. */
/* A 409 whose names the reader has to see, translated rather than echoed.
 *
 * This used to return `error.problem.detail` verbatim for conflicts — the one
 * place in the client that rendered the server's English developer-facing member,
 * held open because FR-67's leg refusal and FR-25's close refusal must NAME what
 * blocks them and `detail` was the only thing carrying the names. That was a hole
 * in AD-27 kept open by two endpoints: a Russian user got English, or the generic
 * sentence with no names in it at all.
 *
 * Both refusals now carry the names in `problem.errors` (`expenses`, `balances`),
 * the shape Story 5.3's branding refusal established and directory's reference
 * refusal widened to a list of records. So the FRAME is translated here and the
 * names print inside it, which is what makes the Russian sentence read as a
 * sentence rather than as a label over English prose (UX-DR-30).
 *
 * Anything else falls through to the ordinary suffix mapping. A conflict carrying
 * no list is still a conflict, and there is nothing to interpolate. */
export function refusalMessage(error: unknown, t: TFunction): string {
  if (error instanceof ApiError && error.code === 'conflict') {
    const named = error.problem.errors
    if (named?.expenses?.length) {
      return t('cases.legs.carriesExpenses', { items: named.expenses.join('; ') })
    }
    if (named?.balances?.length) {
      return t('cases.lifecycle.unsettledBalances', { items: named.balances.join('; ') })
    }
  }
  return t(describeProblem(error).detailKey)
}
